/**
 * Braze SDK Functions (Web)
 */

import {
  automaticallyShowInAppMessages,
  Card,
  changeUser,
  ControlMessage,
  InAppMessage,
  initialize,
  logCardDismissal,
  logContentCardClick,
  logContentCardImpressions,
  logCustomEvent as sdkLogCustomEvent,
  openSession,
  requestContentCardsRefresh as sdkRequestContentCardsRefresh,
  requestImmediateDataFlush as sdkRequestImmediateDataFlush,
  requestPushPermission,
  showInAppMessage,
  subscribeToContentCardsUpdates as sdkSubscribeToContentCardsUpdates,
  subscribeToInAppMessage,
} from '@braze/web-sdk';
import { Href, router } from 'expo-router';
import { BrazeBottomSheetProps } from 'features/home/HomeScreen/components/BrazeBottomSheet/BrazeBottomSheet';
import {
  isDismissedContentCardId,
  markContentCardDismissed,
} from 'services/engagement/braze/helpers/dismissedContentCards';
import {
  transformBrazeBottomSheetCard,
  transformBrazeContentCard,
  transformBrazeSuccessProductOfferCard,
  transformMarketingCarouselCards,
} from 'services/engagement/braze/helpers/transforms.web';
import { isBrazeSuccessProductOfferCard } from 'services/engagement/braze/helpers/typeGuards';
import {
  BrazeContentCard,
  BrazeSuccessProductOfferCard,
  ContentCardsCallback,
} from 'services/engagement/braze/types';
import { Tabs } from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import getEnvironmentVar from 'shared/utils/getEnvironmentVar';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

/**
 * Initialize Braze SDK for web
 */

export function init(): void {
  initialize(getEnvironmentVar('EXPO_BRAZE_WEB_API_KEY'), {
    baseUrl: 'sdk.iad-03.braze.com',
    enableLogging: false,
  });
  requestPushPermission();
  automaticallyShowInAppMessages();
}

/**
 * Request immediate data flush - no-op on web
 */
export function requestImmediateDataFlush(): void {
  // No-op on web
}

/**
 * Update user identity in Braze
 */
export function updateUser(userId: string): void {
  changeUser(userId);
  openSession();
}

/**
 * Register push notification token - no-op on web
 */
export function registerPushToken(_token: string): void {
  // No-op on web - push handled differently
}

/**
 * Log custom event to Braze
 */
export function logCustomEvent(
  eventName: string,
  eventProperties?: object,
): void {
  sdkLogCustomEvent(eventName, eventProperties);
}

/**
 * Launch content cards UI - no-op on web
 */
export function launchContentCardsPress(): void {
  // No-op on web
}

/**
 * Subscribe to content card updates
 */
export function subscribeToContentCardsUpdates(
  callback: ContentCardsCallback,
): void {
  sdkSubscribeToContentCardsUpdates(function (cards) {
    const sdkCards = cards.cards as Card[];
    const marketingCarouselCards = sdkCards
      .map(transformMarketingCarouselCards)
      .filter(isNotNil);

    const brazeContentCards = sdkCards
      .map(transformBrazeContentCard)
      .filter(isNotNil);

    const bottomSheetTemp =
      sdkCards.map(transformBrazeBottomSheetCard).filter(isNotNil)[0] ?? null;
    const homeScreenBottomSheetContent =
      bottomSheetTemp && isDismissedContentCardId(bottomSheetTemp.id)
        ? null
        : bottomSheetTemp;

    const productOfferCards = sdkCards.reduce(
      (acc, card) => {
        const transformedCard = transformBrazeSuccessProductOfferCard(card);
        if (transformedCard) {
          acc[transformedCard.key] = transformedCard.card;
        }
        return acc;
      },
      {} as { [key: string]: BrazeSuccessProductOfferCard },
    );
    callback(
      brazeContentCards,
      homeScreenBottomSheetContent,
      productOfferCards,
      marketingCarouselCards,
    );
  });
}

/**
 * Request content cards refresh
 */
export function requestContentCardsRefresh(): void {
  sdkRequestContentCardsRefresh();
}

/**
 * Subscribe to in-app messages
 */
export function subscribeToInAppMessages(): void {
  subscribeToInAppMessage((inAppMessage: InAppMessage | ControlMessage) => {
    showInAppMessage(inAppMessage);
  });
}

/**
 * Stop Braze subscriptions
 */
export function stopBrazeSubscriptions(): void {
  sdkRequestContentCardsRefresh();
}

export function updatePushNotificationSubscriptionStatus(
  _isEnabled: boolean,
): void {
  // Push subscription is managed natively — no-op on web
}

/**
 * Log content card clicked
 */
export function logContentCardClicked(
  contentCard:
    | BrazeContentCard
    | BrazeSuccessProductOfferCard
    | BrazeBottomSheetProps,
): void {
  const { url, sdkCardWeb } = contentCard;
  if (contentCard && sdkCardWeb) {
    logContentCardClick(sdkCardWeb);
    if (isBrazeSuccessProductOfferCard(contentCard)) {
      // Success screens need to be dismissed to a common ancestor before navigating
      router.dismissTo(Tabs.HOME);
    }
    if (url) {
      router.push(url as Href);
    }
  } else {
    logErrorWithContext('Braze content card object is missing', {
      featureArea: FeatureArea.NOTIFICATIONS,
      responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
      severity: ErrorSeverity.LOW,
    });
  }
}

/**
 * Log content card dismissed
 */
export function logContentCardDismissed(
  contentCard: BrazeContentCard | BrazeBottomSheetProps,
): void {
  const { id, sdkCardWeb } = contentCard;
  if (contentCard && sdkCardWeb && id) {
    if (isDismissedContentCardId(id)) {
      return;
    }
    markContentCardDismissed(id);
    logCardDismissal(sdkCardWeb);
    sdkCardWeb.dismissCard();
    sdkRequestImmediateDataFlush();
  } else {
    logErrorWithContext('Missing SDK card for dismissal', {
      featureArea: FeatureArea.NOTIFICATIONS,
      responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
      severity: ErrorSeverity.LOW,
    });
    return;
  }
}

/**
 * Log content card impression
 */
export function logContentCardImpression(
  contentCard:
    | BrazeContentCard
    | BrazeSuccessProductOfferCard
    | BrazeBottomSheetProps,
): void {
  const { sdkCardWeb } = contentCard;
  if (contentCard && sdkCardWeb) {
    logContentCardImpressions([sdkCardWeb]);
  }
}
